import React, { useState, useEffect } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from "react-i18next";
import axiosInstance from '@/api/axios';

function getColorScheme(amount) {
  if (amount >= 1000) return {
    primary: '#10b981',
    text: 'text-green-600 dark:text-green-400',
    changeBg: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
  };
  if (amount >= 500) return {
    primary: '#3b82f6',
    text: 'text-blue-500 dark:text-blue-400',
    changeBg: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
  };
  return {
    primary: '#f59e0b',
    text: 'text-yellow-600 dark:text-yellow-400',
    changeBg: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
  };
}

export default function WasteDiverted({ selectedBins, selectedTimePeriod, year }) {
  const [animatedAmount, setAnimatedAmount] = useState(0);
  const { t } = useTranslation();

  const { data, isLoading, error } = useQuery({
    queryKey: ['wasteDiverted', selectedBins, selectedTimePeriod, year],
    queryFn: async () => {
      const params = {
        period: selectedTimePeriod,
        year: year,
        'bins[]': selectedBins
      };

      const response = await axiosInstance.get('waste_diverted/', { params });
      return response.data;
    },
    enabled: selectedBins.length > 0,
  });

  useEffect(() => {
    if (!data?.waste_diverted) return;

    const targetAmount = data.waste_diverted;
    if (animatedAmount === targetAmount) return;

    const timeout = setTimeout(() => {
      setAnimatedAmount(prev =>
        prev < targetAmount
          ? Math.min(prev + Math.ceil(targetAmount / 20), targetAmount)
          : Math.max(prev - Math.ceil(targetAmount / 20), targetAmount)
      );
    }, 20);

    return () => clearTimeout(timeout);
  }, [animatedAmount, data?.waste_diverted]);

  const colorScheme = getColorScheme(data?.waste_diverted || 0);
  const isNegative = data?.change_rate < 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-gray-800 p-2 border dark:border-gray-700 rounded-lg shadow text-xs">
          <p className="font-bold dark:text-gray-200">{data.date}</p>
          <p className="text-green-600 dark:text-green-400">{t('wasteDiverted.tooltip.diverted')}: {data.diverted} kg</p>
          <p className="text-gray-500 dark:text-gray-400">{t('wasteDiverted.tooltip.residual')}: {data.residual} kg</p>
          <p className="text-gray-500 dark:text-gray-400">{t('wasteDiverted.tooltip.total')}: {data.total} kg</p>
        </div>
      );
    }
    return null;
  };

  if (isLoading) return (
    <div className="rounded-lg border bg-card text-card-foreground w-full h-[200px] flex items-center justify-center">
      {t('wasteDiverted.loading')}
    </div>
  );

  if (error) return (
    <div className="rounded-lg border bg-card text-card-foreground w-full h-[200px] flex items-center justify-center">
      {t('wasteDiverted.error')}
    </div>
  );

  if (!data || selectedBins.length === 0) return (
    <div className="rounded-lg border bg-card text-card-foreground w-full h-[200px] flex items-center justify-center">
      {selectedBins.length === 0 ? t('wasteDiverted.selectBins') : t('wasteDiverted.noData')}
    </div>
  );

  return (
    <div className="rounded-lg border bg-card text-card-foreground dark:hover:bg-slate-900 hover:cursor-pointer flex flex-col w-full h-[200px]">
      <div className="px-4 py-3 flex items-center justify-between">
        <h2 className="tracking-tight text-sm font-medium">{t('wasteDiverted.title')}</h2>
      </div>

      <div className="px-4 pb-1 text-2xl font-bold text-green-600 dark:text-green-400">
        {animatedAmount.toLocaleString()} kg
      </div>

      <div className="flex-1 w-full px-0 pt-0 overflow-hidden rounded-b-lg">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data.historical_data}
            margin={{
              top: 5,
              right: 0,
              left: 0,
              bottom: 0,
            }}
          >
            <XAxis
              dataKey="date"
              hide
            />
            <YAxis
              hide
            />
            <Tooltip content={<CustomTooltip />} wrapperStyle={{ outline: 'none' }} />
            <Bar
              dataKey="diverted"
              name="Diverted Waste"
              radius={[4, 4, 0, 0]}
            >
              {data.historical_data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill="#10b981" />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}